import { pxToRem } from '@/Client/Styles/pxToRem';
import { css } from '@emotion/react';
import { ScopeReportLineItemWithChangeData } from '@kaiizen/kai-pro-api';
import {
  applyAttentionSummaryFiltersToLineItems,
  AssignedContractorsMap,
  AttentionSummaryFilterType,
  extractPhotosFromLineItems,
  formatCurrency,
  GroupedReportLineItems,
  partitionDisplayNameAccessor,
  partitionLineItems,
  PartitionMode,
  ScopeReportLineItem,
  ScopeReportOverview,
} from '@kaiizen/kai-reports';
import { useMemo, useState } from 'react';
import { AttentionSummaryContainer } from './AttentionSummaryContainer';
import { CoverPage } from './CoverPage';
import { PhotosPage } from './PhotosPage';
import { useScopePrintPreviewContext } from './ScopePrintPreviewContext';
import { TradePage } from './TradePage';

const BORDERED_CONTAINER = css`
  border: 3px solid #1D1D1D;
  margin-bottom: 40px;

  @media print {
    margin-bottom: 0;
  }
`;

const BLACK_HEADER = css`
  background-color: #1D1D1D;
  color: white;
  padding: 15px 20px;
  font-family: CircularXXWeb-Bold, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: bold;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const BLACK_HEADER_SUMMARY = css`
  background-color: #1D1D1D;
  color: white;
  padding: 15px 20px;
  font-family: CircularXXWeb-Bold, sans-serif;
  font-size: ${pxToRem(16)};
  font-weight: bold;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
`;

const HEADER_STRIKETHROUGH = css`
  text-decoration: line-through;
  opacity: 0.7;
`;

const HEADER_SEPARATOR = css`
  color: white;
  opacity: 0.5;
`;

const CONTENT_WRAPPER = css`
  padding: 20px;
`;

export const ReportSection = ({
  overviewData,
  sowData,
  assignedContractorsWithTrades,
  logoSvg,
  includePhotos = true,
  isChangeOrderReport = false,
  diffReportOverviewData,
  diffScopeLineItems,
  propertyProjectId,
  partitionType = 'group',
}: {
  overviewData: ScopeReportOverview;
  sowData: ScopeReportLineItem[];
  assignedContractorsWithTrades: AssignedContractorsMap;
  logoSvg?: string;
  includePhotos?: boolean;
  isChangeOrderReport?: boolean;
  diffReportOverviewData?: {
    currentTotalCost: number;
    baselineTotalCost: number;
    totalDifference: number;
  };
  diffScopeLineItems?: ScopeReportLineItemWithChangeData[] | null;
  propertyProjectId?: string;
  partitionType?: PartitionMode;
}) => {
  const {
    scan,
    scanLifecycleStatus,
    reportType,
  } = useScopePrintPreviewContext();
  const isFullScopeReport = reportType === 'fullScope';
  const isCloseoutReport = reportType === 'closeout';
  const showAttentionSummary = isFullScopeReport || isCloseoutReport;

  const [activeFilters, setActiveFilters] = useState<Set<AttentionSummaryFilterType>>(new Set());

  const handleFilterToggle = (filterType: AttentionSummaryFilterType) => {
    setActiveFilters(previousFilters => {
      if (previousFilters.has(filterType)) {
        return new Set();
      }
      return new Set([filterType]);
    });
  };

  const filteredSowData = useMemo(
    () => applyAttentionSummaryFiltersToLineItems(sowData, activeFilters, diffScopeLineItems),
    [sowData, activeFilters, diffScopeLineItems],
  );

  const partitionedSowData = useMemo<GroupedReportLineItems>(
    () => partitionLineItems(sowData, partitionType),
    [sowData, partitionType],
  );

  const partitionedDiffSowData = useMemo(
    () => diffScopeLineItems ? partitionLineItems(diffScopeLineItems, partitionType) : undefined,
    [diffScopeLineItems, partitionType],
  );

  const partitionedSowDataToRender = useMemo(() => {
    const partitionedFiltered = partitionLineItems(filteredSowData, partitionType);
    const getDisplayName = partitionDisplayNameAccessor[partitionType];

    return Object.entries(partitionedFiltered).map(([key, items]) => ({
      key,
      items,
      roomArea: items[0]?.groupRoomArea,
      id: items[0]?.groupId,
      displayName: getDisplayName(items[0]),
    }));
  }, [filteredSowData, partitionType]);

  const hasContent = partitionedSowDataToRender.length > 0;

  return (
    <>
      <CoverPage
        overviewData={overviewData}
        assignedContractorsWithTrades={assignedContractorsWithTrades}
        logoSvg={logoSvg}
        reportType={reportType}
        scanLifecycleStatus={scanLifecycleStatus}
        scanId={scan?.id}
        scanUpdatedAt={scan?.updatedAt}
        propertyProjectId={propertyProjectId}
      />

      {showAttentionSummary && (
        <AttentionSummaryContainer
          sowData={partitionedSowData}
          activeFilters={activeFilters}
          onFilterToggle={handleFilterToggle}
          diffScopeLineItems={partitionedDiffSowData}
        />
      )}

      {!hasContent && (
        <div css={BORDERED_CONTAINER}>
          <div css={CONTENT_WRAPPER}>
            No line items scoped yet
          </div>
        </div>
      )}

      {hasContent && (
        <div css={BORDERED_CONTAINER}>
          {isChangeOrderReport && diffReportOverviewData
            ? (
              <div css={BLACK_HEADER_SUMMARY}>
                <span css={HEADER_STRIKETHROUGH}>
                  Current Total Amount: {formatCurrency(diffReportOverviewData.baselineTotalCost || 0)}
                </span>
                <span css={HEADER_SEPARATOR}>|</span>
                <span>
                  Difference: {diffReportOverviewData.totalDifference > 0 ? '+' : ''}
                  {formatCurrency(diffReportOverviewData.totalDifference || 0)}
                </span>
                <span css={HEADER_SEPARATOR}>|</span>
                <span>
                  New Total Amount: {formatCurrency(diffReportOverviewData.currentTotalCost || 0)}
                </span>
              </div>
            )
            : (
              <div css={BLACK_HEADER}>
                Total Amount: {formatCurrency(overviewData?.totalProjectCost || 0)}
              </div>
            )}
          <div css={CONTENT_WRAPPER}>
            {partitionedSowDataToRender.map(partition => {
              const photos = extractPhotosFromLineItems(partition.items);
              const groupTotal = partition.items.reduce((sum, item) => sum + (item.totalLineItemCost || 0), 0);

              return (
                <div key={partition.key} id={partition.id ? `group-${partition.id}` : undefined}>
                  <TradePage
                    tradeName={partition.displayName}
                    items={partition.items}
                    groupRoomArea={partition.roomArea}
                    groupTotal={groupTotal}
                    startIndex={0}
                    isChangeOrderReport={isChangeOrderReport}
                  />

                  {includePhotos && photos.length > 0 && (
                    <PhotosPage
                      tradeName={partition.displayName}
                      photos={photos}
                      groupRoomArea={partition.roomArea}
                      groupTotal={groupTotal}
                      groupId={partition.id || ''}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
